import { selectStageReviewerProgressEnabled } from './project-reviewer-progress.selectors';
import { selectScreeningInfoEnabled } from '@app/screening/screening-overview/screening-overview-statistics.selectors';
import { selectStageOverviewBundleEnabled } from '@app/stage/stage-overview/stage-overview-statistics.selectors';
import { selectScreeningOverviewEnabled } from './project-screening-overview.selectors';
import { selectProjectReviewerProgressEnabled } from '@app/project/project-overview/project-reviewer-progress.state';
import { RuntimeFeatureFlagsService } from '@core/services/runtime-feature-flags.service';
import { inject } from '@angular/core';
import {
  CanMatchFn,
  PartialMatchRouteSnapshot,
  Route,
  UrlSegment,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { mapFn, throwErrorExp } from '@core/actions/util';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { RequestStatus } from '@core/signal-store-features/request-state.feature';
import { entityKey } from '@core/state/entities/entity-name-map';
import {
  selectLoadedProjectId,
  selectLoadingStatusForProject,
  selectProjectIsLoading,
  selectReviewerScreeningStatsStatus,
} from '@core/state/ui/project/project-ui.selectors';
import { LoadProjectRequest } from '@core/requests';
import { selectStageReviewerAnnotationStatsStatusMap } from '@core/state/ui/stage/stage-ui.selectors';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { v4 as uuid } from 'uuid';
import { combineLatest, filter, firstValueFrom, map, take } from 'rxjs';
import {
  ProjectMemberStatus,
  selectAreMembershipStatsLoadedOrLoadingForProject,
  selectAreProjectStatsLoadedOrLoadingForProject,
  selectAreReviewerStatsLoadedOrLoadingForProject,
  selectMemberStatusForProject,
  selectProjectById,
} from '@core/state/entities/project/project.selectors';
import { ProjectLoadingStatus } from '@core/state/ui/project/project-ui.reducer';
import { selectProjectDetailsForProject } from '@core/state/entities/project-details/project-details.selectors';

/**
 * True when the remaining URL segments resolve to the Project Overview route: the redirect-empty
 * path (`/projects/:projectId`, which redirects to `detail`) or `detail` explicitly, with nothing
 * beyond it. Mirrors how `project-statistics-route.guard.ts` classifies the Overview destination
 * by its `detail` route-config path, but this `CanMatchFn` runs during route matching, before a
 * route tree exists to walk — so it reads the raw segments instead.
 */
export function isProjectOverviewDestination(segments: UrlSegment[]): boolean {
  const rest = segments.slice(1);
  return rest.length === 0 || (rest.length === 1 && rest[0].path === 'detail');
}

export function isStageReviewDestination(segments: UrlSegment[]): boolean {
  return (
    segments[1]?.path === 'stage' &&
    (segments.length === 3 ||
      segments[3]?.path === 'review' ||
      segments[3]?.path === 'reconcile')
  );
}

export const projectCanMatchGuard: CanMatchFn = async (
  route: Route,
  segments: UrlSegment[],
  _currentSnapshot: PartialMatchRouteSnapshot
) => {
  const store = inject(Store);
  const runtimeFlags = inject(RuntimeFeatureFlagsService);
  const loadProjectRequest = inject(LoadProjectRequest);
  const projectId = segments[0].path;

  const $projectDetailsForProject = store.selectSignal(
    selectProjectDetailsForProject(projectId)
  );
  const $currentLoadedProjectId = store.selectSignal(selectLoadedProjectId);
  const $areReviewerStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreReviewerStatsLoadedOrLoadingForProject(projectId)
  );
  const $areMembershipStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreMembershipStatsLoadedOrLoadingForProject(projectId)
  );
  const $areProjectStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreProjectStatsLoadedOrLoadingForProject(projectId)
  );

  const $memberStatus = store.selectSignal(
    selectMemberStatusForProject(projectId)
  );

  if (
    $memberStatus() === ProjectMemberStatus.Idle ||
    $memberStatus() === ProjectMemberStatus.Error
  ) {
    loadProjectRequest.dispatchRequest.loadProject({
      projectId,
    });
  }
  if ($memberStatus() === ProjectMemberStatus.Loading) {
    await firstValueFrom(
      store
        .select(selectMemberStatusForProject(projectId))
        .pipe(
          filter((memberStatus) => memberStatus !== ProjectMemberStatus.Loading)
        )
    );
  }

  const onIsMember = async () => {
    await runtimeFlags.initialSnapshotReady;
    // Dispatch stat loading actions if needed. With the own-progress consumer on AND the
    // destination the Project Overview, ProjectOverviewComponent's ProjectReviewerProgressState is
    // the only reader of this endpoint; it restores the legacy load itself on rollback or a
    // legacy-shaped answer. ProjectReviewerProgressState is provided only there, so every other
    // project child route (e.g. `stage/:id/review/completed`, `stage/:id/review/:studyId`, which
    // read the global reviewer selectors directly) keeps the legacy load regardless of the flag.
    if (
      !(
        (store.selectSignal(selectProjectReviewerProgressEnabled)() &&
          isProjectOverviewDestination(segments)) ||
        (store.selectSignal(selectStageReviewerProgressEnabled)() &&
          isStageReviewDestination(segments))
      ) &&
      !$areReviewerStatsLoadedOrLoadingForProject()
    ) {
      store.dispatch(
        projectDetailActions.retrieveProjectReviewerStats({
          projectId,
          correlationId: uuid(),
        })
      );
    }
    if (
      !store.selectSignal(selectScreeningOverviewEnabled)() &&
      !store.selectSignal(selectStageOverviewBundleEnabled)() &&
      !store.selectSignal(selectScreeningInfoEnabled)() &&
      !store.selectSignal(selectStageReviewerProgressEnabled)() &&
      (!$areMembershipStatsLoadedOrLoadingForProject() ||
        !$areProjectStatsLoadedOrLoadingForProject())
    ) {
      store.dispatch(
        projectDetailActions.retrieveFullProjectStats({
          projectId,
          correlationId: uuid(),
        })
      );
    }

    if (
      $currentLoadedProjectId() !== projectId ||
      !$projectDetailsForProject()
    ) {
      // Dispatch request to load project
      loadProjectRequest.dispatchRequest.loadProject({
        projectId,
      });

      // Wait for either success (data appears) or failure (error status)
      // This handles the race condition where multiple requests might be exhausted
      const canAccess = await firstValueFrom(
        combineLatest([
          store.select(selectProjectDetailsForProject(projectId)),
          store.select(selectLoadingStatusForProject(projectId)),
        ]).pipe(
          // Wait until loading is complete (either success or error)
          filter(
            ([details, loadingStatus]) =>
              details !== null ||
              loadingStatus === ProjectLoadingStatus.Error ||
              loadingStatus === ProjectLoadingStatus.NotFound
          ),
          // Map to access decision
          map(([details, loadingStatus]) => {
            // Deny access if load failed
            if (
              loadingStatus === ProjectLoadingStatus.Error ||
              loadingStatus === ProjectLoadingStatus.NotFound
            ) {
              return false;
            }
            // Allow access if details loaded successfully
            return details !== null;
          }),
          take(1)
        )
      );

      return canAccess;
    } else {
      return true;
    }
  };

  const memberStatus = $memberStatus();

  return memberStatus === ProjectMemberStatus.Member
    ? await onIsMember()
    : memberStatus === ProjectMemberStatus.NotMember ||
        memberStatus === ProjectMemberStatus.NotFound ||
        memberStatus === ProjectMemberStatus.Error
      ? false
      : throwErrorExp('Unexpected member status');
};
